import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Clock } from "lucide-react";

interface Props {
  /** ISO date (YYYY-MM-DD) or full ISO timestamp */
  targetDate: string;
  /** Optional wall-clock start time in HH:mm or HH:mm:ss (interpreted in `timezone`) */
  startTime?: string | null;
  /** IANA timezone (e.g. "America/New_York"). Common US abbreviations are also accepted. */
  timezone?: string | null;
  label?: string;
}

// Map common US timezone abbreviations to IANA zones (handles DST automatically)
const TZ_ABBREVIATIONS: Record<string, string> = {
  EST: "America/New_York",
  EDT: "America/New_York",
  ET: "America/New_York",
  CST: "America/Chicago",
  CDT: "America/Chicago",
  CT: "America/Chicago",
  MST: "America/Denver",
  MDT: "America/Denver",
  MT: "America/Denver",
  PST: "America/Los_Angeles",
  PDT: "America/Los_Angeles",
  PT: "America/Los_Angeles",
  AKST: "America/Anchorage",
  AKDT: "America/Anchorage",
  HST: "Pacific/Honolulu",
};

const normalizeTimezone = (tz?: string | null): string | null => {
  if (!tz) return null;
  const trimmed = tz.trim();
  if (!trimmed) return null;
  if (TZ_ABBREVIATIONS[trimmed.toUpperCase()]) return TZ_ABBREVIATIONS[trimmed.toUpperCase()];
  // Validate IANA
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: trimmed });
    return trimmed;
  } catch {
    return null;
  }
};

/**
 * Convert a wall-clock date/time in a given IANA timezone to a UTC epoch ms.
 * Works across DST boundaries.
 */
const zonedWallClockToEpoch = (
  dateStr: string,
  timeStr: string,
  timeZone: string,
): number => {
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [hh = 0, mm = 0, ss = 0] = timeStr.split(":").map(Number);
  // Guess a UTC instant, then correct using the actual offset that timezone has at that instant.
  const guess = Date.UTC(y, (mo || 1) - 1, d || 1, hh, mm, ss);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = fmt.formatToParts(new Date(guess)).reduce<Record<string, string>>((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour === "24" ? "0" : parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  const offset = asUtc - guess; // ms that the zone is ahead of UTC at that instant
  return guess - offset;
};

const resolveTargetEpoch = (
  targetDate: string,
  startTime?: string | null,
  timezone?: string | null,
): number => {
  // Full ISO with explicit zone/offset
  if (targetDate.length > 10 && /[zZ]|[+-]\d\d:?\d\d$/.test(targetDate)) {
    return new Date(targetDate).getTime();
  }
  const datePart = targetDate.slice(0, 10);
  const timePart = (startTime && startTime.trim()) || (targetDate.length > 10 ? targetDate.slice(11, 19) : "00:00:00");
  const tz = normalizeTimezone(timezone);
  if (tz) return zonedWallClockToEpoch(datePart, timePart, tz);
  // Fallback: treat as local wall clock
  return new Date(`${datePart}T${timePart}`).getTime();
};

const calc = (target: number) => {
  const diff = target - Date.now();
  if (diff <= 0) return { done: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    done: false,
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
};

export const CountdownTimer = ({
  targetDate,
  startTime,
  timezone,
  label = "Conference begins in",
}: Props) => {
  const target = useMemo(
    () => resolveTargetEpoch(targetDate, startTime, timezone),
    [targetDate, startTime, timezone],
  );

  const [t, setT] = useState(() => calc(target));
  const timeoutRef = useRef<number | null>(null);
  // Track the last minute we announced to screen readers (avoid per-second SR spam)
  const lastAnnouncedMinuteRef = useRef<number>(-1);
  const [announcement, setAnnouncement] = useState<string>("");

  useEffect(() => {
    if (Number.isNaN(target)) return;

    let cancelled = false;

    // Self-correcting tick: schedule next update aligned to the next wall-clock second.
    // This avoids drift over long sessions and recovers automatically after the tab
    // is throttled in the background.
    const tick = () => {
      if (cancelled) return;
      const next = calc(target);
      setT(next);
      if (next.done) return;
      const now = Date.now();
      const delay = 1000 - (now % 1000);
      timeoutRef.current = window.setTimeout(tick, delay);
    };

    tick();

    // Re-sync immediately when the tab becomes visible again (background throttling).
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current);
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [target]);

  // Throttled SR announcement: every minute (or on completion)
  useEffect(() => {
    const totalMinutes = t.done ? 0 : t.days * 1440 + t.hours * 60 + t.minutes;
    if (totalMinutes !== lastAnnouncedMinuteRef.current) {
      lastAnnouncedMinuteRef.current = totalMinutes;
      if (t.done) {
        setAnnouncement(`${label}: the conference has started.`);
      } else {
        const parts: string[] = [];
        if (t.days) parts.push(`${t.days} day${t.days === 1 ? "" : "s"}`);
        if (t.hours) parts.push(`${t.hours} hour${t.hours === 1 ? "" : "s"}`);
        parts.push(`${t.minutes} minute${t.minutes === 1 ? "" : "s"}`);
        setAnnouncement(`${label}: ${parts.join(", ")} remaining.`);
      }
    }
  }, [t, label]);

  if (Number.isNaN(target)) return null;

  const fullText = t.done
    ? "Conference has started"
    : `${t.days} days, ${t.hours} hours, ${t.minutes} minutes, and ${t.seconds} seconds`;

  const Box = ({ value, unit }: { value: number; unit: string }) => (
    <div
      className="flex flex-col items-center bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[64px] border border-white/20"
      aria-hidden="true"
    >
      <span className="text-2xl md:text-3xl font-bold tabular-nums text-white">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-white/80">{unit}</span>
    </div>
  );

  return (
    <Card
      className="border-0 bg-gradient-to-r from-sky-600 to-amber-500 text-white shadow-lg focus-within:ring-2 focus-within:ring-white/70"
      tabIndex={0}
      role="timer"
      aria-label={`${label}: ${fullText}`}
    >
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4" aria-hidden="true" />
          <p className="text-sm font-medium">{t.done ? "Conference has started!" : label}</p>
        </div>
        {!t.done && (
          <div className="flex gap-2 justify-between">
            <Box value={t.days} unit="Days" />
            <Box value={t.hours} unit="Hours" />
            <Box value={t.minutes} unit="Mins" />
            <Box value={t.seconds} unit="Secs" />
          </div>
        )}
        {/* Polite live region — announces approximately every minute */}
        <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {announcement}
        </span>
      </CardContent>
    </Card>
  );
};

export default CountdownTimer;
